import { SITE_ORIGIN } from './siteConfig';

const META_MAX = 140;

const CTR_PRIORITY_SLUGS = new Set([
  'plymouth',
  'ipswich',
  'derby',
  'torbay',
  'newton-abbot',
  'exeter',
  'glasgow',
  'truro'
]);

export const TOP_LANDING_TOWN_SLUGS = new Set([
  ...CTR_PRIORITY_SLUGS,
  'london',
  'birmingham'
]);

const CTR_DESC_HARD_MAX = 158;

export function constructionTradeKnowsAbout() {
  return ['Construction & Trade Websites'];
}

export function isTopLandingTownSlug(slug) {
  return slug && TOP_LANDING_TOWN_SLUGS.has(String(slug).toLowerCase());
}

function highImpressionTownMetaDescription(area) {
  const town = area.name;
  let s = `Affordable website & website maintenance for ${town}: bespoke code, SSL, hosting & care from £19.99/mo—no £2,000 upfront bill. PageSpeed-aware builds & SEO-ready structure — WebCodeCraft.`;
  if (s.length > CTR_DESC_HARD_MAX) {
    s = `${town}: affordable website + website maintenance from £19.99/mo, no big upfront fee. Fast, SEO-ready builds — WebCodeCraft.`;
  }
  if (s.length > CTR_DESC_HARD_MAX) s = s.slice(0, CTR_DESC_HARD_MAX - 1).trimEnd() + '…';
  return s;
}

export function webcodecraftProfessionalServiceSchemaId() {
  return `${SITE_ORIGIN}/#webcodecraft-professional-service`;
}

const TITLE_MAX = 72;

const SW_COUNTIES = new Set(['Devon', 'Cornwall']);

function slugHash(slug) {
  let n = 0;
  for (let i = 0; i < slug.length; i += 1) n += slug.charCodeAt(i);
  return n;
}

function isSouthWestCounty(county) {
  return county && SW_COUNTIES.has(county);
}

export function isCtrPriorityLocationSlug(slug) {
  return slug && CTR_PRIORITY_SLUGS.has(String(slug).toLowerCase());
}

function ctrPriorityTitle(area) {
  const t = `${area.name} Web Design | £0 Upfront | 5-Star Rated`;
  return t.length <= TITLE_MAX ? t : t.slice(0, TITLE_MAX - 1).trimEnd() + '…';
}

function ctrPriorityMetaDescription(area) {
  const town = area.name;
  let s = `Need a professional site in ${town}? Get a custom build + hosting + maintenance for just £19.99/mo. Optimised PageSpeed & SEO-ready. No massive upfront fees. Join our 5-star local network today!`;
  if (s.length > CTR_DESC_HARD_MAX) {
    s = `Need a professional site in ${town}? Custom build + hosting from £19.99/mo. Optimised PageSpeed & SEO. No upfront fees. Join our 5-star local network — WebCodeCraft.`;
  }
  if (s.length > CTR_DESC_HARD_MAX) {
    s = `Professional site in ${town}? From £19.99/mo: custom build + hosting + care. Optimised speed & SEO. No big upfront fees. 5-star rated — WebCodeCraft.`;
  }
  if (s.length > CTR_DESC_HARD_MAX) s = s.slice(0, CTR_DESC_HARD_MAX - 1).trimEnd() + '…';
  return s;
}

function southWestCtrMetaDescription(area) {
  const town = area.name;
  const county = area.county || '';
  const v = slugHash(area.slug) % 5;
  const variants = [
    `Need a professional site in ${town}, ${county}? Get a custom build + hosting + maintenance for just £19.99/mo. Optimised PageSpeed & SEO-ready. No massive upfront fees. Join our 5-star local network today!`,
    `£19.99/mo in ${town} & across ${county}: custom website + hosting + care. Optimised speed & SEO. No big upfront fees. 5-star rated — WebCodeCraft.`,
    `Custom web design in ${town} (${county}): build + hosting from £19.99/mo. PageSpeed-optimised & SEO-ready. £0 upfront on published plans. 5-star local service — WebCodeCraft.`,
    `Need a new site in ${town}? From £19.99/mo—bespoke code, hosting & maintenance in ${county}. Optimised performance. No agency-style upfront bill. Join our 5-star network today!`,
    `Professional websites for ${town} & nearby ${county}: £19.99/mo build + hosting + care. SEO-ready & fast. £0 upfront. Five-star rated — WebCodeCraft.`
  ];
  let s = variants[v] || variants[0];
  if (s.length > CTR_DESC_HARD_MAX) {
    s = `Need a professional site in ${town}, ${county}? Custom build + hosting from £19.99/mo. Optimised PageSpeed & SEO. No upfront fees. 5-star local network — WebCodeCraft.`;
  }
  if (s.length > CTR_DESC_HARD_MAX) {
    s = `£19.99/mo: custom site + hosting + care in ${town}, ${county}. Optimised & SEO-ready. £0 upfront. 5-star — WebCodeCraft.`;
  }
  if (s.length > CTR_DESC_HARD_MAX) s = s.slice(0, CTR_DESC_HARD_MAX - 1).trimEnd() + '…';
  return s;
}

export function localAreaClickyTitle(area) {
  if (!area || !area.name) return '';
  const slugLo = String(area.slug || '').toLowerCase();
  if (slugLo === 'plymouth' || slugLo === 'ipswich') {
    const t = `Web Design in ${area.name} | £19.99/mo | No Upfront Cost`;
    return t.length <= TITLE_MAX ? t : t.slice(0, TITLE_MAX - 1).trimEnd() + '…';
  }
  if (isCtrPriorityLocationSlug(area.slug) || isSouthWestCounty(area.county)) return ctrPriorityTitle(area);
  const legacy = `${area.name} Web Design & Development | £0 Upfront · Low Monthly`;
  if (legacy.length <= TITLE_MAX) return legacy;
  const short = `${area.name} | £0 Upfront · Low Monthly Hosting & Care`;
  return short.length <= TITLE_MAX ? short : `${area.name} | £0 Upfront · WebCodeCraft`;
}

export function localAreaCtaMetaDescription(area) {
  if (!area || !area.name) return '';
  const slugLo = String(area.slug || '').toLowerCase();
  if (slugLo === 'plymouth' || slugLo === 'ipswich') return highImpressionTownMetaDescription(area);
  const county = area.county || '';
  if (isSouthWestCounty(county)) return southWestCtrMetaDescription(area);
  if (isCtrPriorityLocationSlug(area.slug)) return ctrPriorityMetaDescription(area);
  const town = area.name;
  let s = `FREE SEO audit · ${town}${county ? ` & ${county}` : ''}: bespoke websites (custom builds, not WordPress). £0 upfront; low monthly for build, hosting & maintenance. SSL. WebCodeCraft UK.`;
  if (s.length > META_MAX) {
    s = `FREE SEO audit · ${town}: bespoke sites, not WordPress. £0 upfront, low monthly care & SSL — WebCodeCraft.`;
  }
  if (s.length > META_MAX) s = s.slice(0, META_MAX - 1).trimEnd() + '…';
  return s;
}

export function localAreaAggregateRating(aggregateId) {
  const node = {
    '@type': 'AggregateRating',
    ratingValue: 5,
    reviewCount: 2,
    bestRating: 5,
    worstRating: 1
  };
  if (aggregateId) {
    node['@id'] = aggregateId;
  }
  return node;
}

export function tradesLocalBusinessAggregateRating(aggregateId) {
  const node = {
    '@type': 'AggregateRating',
    ratingValue: '5',
    reviewCount: '24',
    bestRating: '5',
    worstRating: '1'
  };
  if (aggregateId) {
    node['@id'] = aggregateId;
  }
  return node;
}

export function localAreaPublishedReviews() {
  return [
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Client, new business' },
      reviewBody:
        'I was needing a website for my new business. The whole process was made so simple by Shane—I am absolutely delighted with what you have done for me and the ongoing support.',
      reviewRating: { '@type': 'Rating', ratingValue: 5, bestRating: 5, worstRating: 1 }
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Lewis Jones' },
      reviewBody: 'Really good experience and service with helping me get my website up and going.',
      reviewRating: { '@type': 'Rating', ratingValue: 5, bestRating: 5, worstRating: 1 }
    }
  ];
}
